"use client";

import { Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    question: "Wie lange dauert die Erstellung einer Website?",
    answer: "Die Dauer hängt vom Umfang Ihres Projekts ab. Eine einfache Landingpage ist oft innerhalb von zwei Wochen fertig, während komplexere Unternehmenswebsites etwa vier bis sechs Wochen in Anspruch nehmen. Wir legen Wert auf Qualität und eine saubere technische Umsetzung."
  }, {
    question: "Bieten Sie auch SEO-Optimierung an?",
    answer: "Ja, SEO ist ein fester Bestandteil unserer Arbeit. Wir achten bereits bei der Konzeption auf eine saubere Struktur und relevante Inhalte, damit Ihre Website von Suchmaschinen wie Google optimal gefunden wird."
  }, {
    question: "Was passiert nach der Veröffentlichung?",
    answer: "Wir lassen Sie nicht allein. Auf Wunsch übernehmen wir die regelmäßige Wartung, Sicherheitsupdates und technische Pflege Ihrer Website, damit Sie sich voll auf Ihr Kerngeschäft konzentrieren können."
  }, {
    question: "Kann ich meine Inhalte selbst bearbeiten?",
    answer: "Absolut. Wir setzen auf benutzerfreundliche Content-Management-Systeme, die es Ihnen ermöglichen, Texte und Bilder einfach selbst anzupassen, ohne dass Sie Programmierkenntnisse benötigen."
  }, {
    question: "Was kostet eine professionelle Website?",
    answer: "Jedes Projekt ist individuell. Nach einem ersten Gespräch erstellen wir Ihnen ein transparentes Angebot, das genau auf Ihre Bedürfnisse und Ziele zugeschnitten ist. Qualität und ein faires Preis-Leistungs-Verhältnis stehen bei uns an erster Stelle."
  }, {
    question: "Arbeiten Sie auch mit Kunden außerhalb von Oldenburg?",
    answer: "Ja, wir betreuen Kunden aus ganz Deutschland. Dank moderner Kommunikationstools sind wir flexibel und können Projekte effizient und professionell remote abwickeln, egal wo Ihr Unternehmen ansässig ist."
  }, {
    question: "Wie läuft der Prozess ab?",
    answer: "Wir starten mit einer fundierten Analyse, gefolgt von einem maßgeschneiderten Konzept. Nach Ihrer Freigabe gehen wir in die Umsetzung und begleiten Sie bis zur erfolgreichen Veröffentlichung Ihrer neuen Website."
  }, {
    question: "Benötige ich ein eigenes Hosting?",
    answer: "Wir beraten Sie gerne bei der Wahl des passenden Hostings. Falls Sie noch keinen Anbieter haben, empfehlen wir Ihnen zuverlässige Partner, die für Geschwindigkeit und Sicherheit sorgen."
  }];
  return <section data-section-id="995" id="häufige-fragen" className="relative overflow-hidden py-32 md:py-40 bg-background text-foreground">
      {}
      <div className="pointer-events-none absolute -bottom-10 -right-10 hidden md:block" style={{
      opacity: 0.05
    }} aria-hidden="true">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716638/img/orvo_web_pattern.png" alt="" className="h-[460px] w-[460px] object-contain" loading="lazy" decoding="async" />
      </div>

      <div ref={ref} className={`relative mx-auto max-w-4xl px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="flex justify-center">
          <span className="font-default text-xs tracking-[0.25em] uppercase" style={{
          color: "hsl(43 50% 55%)"
        }}>
            Häufige Fragen &mdash; FAQ
          </span>
        </div>

        {}
        <h2 className="mt-8 text-center font-default italic font-light leading-[1.05] text-[44px] sm:text-[52px] md:text-[64px]">
          Fragen
          <br />
          vor der
          <br />
          Zusammenarbeit.
        </h2>

        {}
        <div className="mx-auto mt-16 max-w-[880px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b-0" style={{
            borderBottom: "1px solid hsl(43 20% 25%)"
          }}>
                <AccordionTrigger className="group py-7 hover:no-underline [&>svg]:hidden">
                  <span className="font-default italic font-light text-xl md:text-[22px] leading-snug pr-4">
                    {faq.question}
                  </span>
                  <span className="ml-auto flex h-8 w-8 shrink-0 items-center justify-center text-2xl font-light transition-transform duration-300 ease-out group-data-[state=open]:rotate-45" style={{
                color: "hsl(43 50% 55%)"
              }} aria-hidden="true">
                    +
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-7 pr-12">
                  <p className="font-default text-base leading-[1.7] max-w-[720px] text-muted-foreground">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {}
        <div className="mt-20 flex justify-center">
          <div className="w-full max-w-xl rounded-lg px-8 py-10 text-center" style={{
          backgroundColor: "hsl(0 0% 9% / 0.5)",
          border: "1px solid hsl(43 20% 25%)"
        }}>
            <p className="font-default italic text-lg md:text-xl leading-relaxed">
              Haben Sie weitere Fragen?
              <br />
              Wir beraten Sie gerne persönlich.
            </p>
            <div className="mt-7">
              <Button asChild variant="outline" size="lg" className="font-default text-xs tracking-[0.2em] uppercase bg-transparent hover:bg-primary hover:text-primary-foreground rounded-lg" style={{
              borderColor: "hsl(43 50% 55%)",
              color: "hsl(43 50% 55%)"
            }}>
                <Link to="/ContactPage">
                  <Mail className="size-4 mr-2" />
                  Jetzt Kontakt aufnehmen
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}